// Authenticated-user gate. Blocks the entire app tree until Kinde has
// finished its handshake AND the user's org is in our role registry,
// then exposes the user + role + logout function via ``UserContext``.
// Mirrors ``admin-app/src/auth/UserContext.tsx``.
//
// On first render, if Kinde reports no user we fire ``login()`` with
// ``org_code`` set as a hint to the authorisation server. That's only
// a hint though - users not in that org can still authenticate against
// a different org, which is why we re-check the issued token's
// org_code on the way back in and force a logout if it isn't on our
// allowlist. See web/src/auth/roles.ts for the org -> role registry.
//
// The path the user was on before sign-in is preserved in
// ``app_state.redirectTo`` and read back by ``LoginRedirect.tsx``
// after the post-login callback lands.
import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import { useEffect, type ReactNode } from "react";

import { KINDE_CONFIG } from "./kindeConfig";
import { roleForOrgCode } from "./roles";
import { UserContext } from "./UserContext";

const Loading = ({ label }: { label: string }) => (
  <div
    style={{
      padding: "2rem",
      fontFamily: "var(--font-sans, system-ui)",
      color: "var(--ir-navy, #1a2244)",
    }}
  >
    {label}
  </div>
);

export const UserProvider = ({ children }: { children: ReactNode }) => {
  const { isLoading, user, login, logout, getOrganization } = useKindeAuth();

  // Fire login() once auth finishes loading if there's no user yet.
  // ``useEffect`` rather than an inline call so React doesn't trigger
  // an infinite re-render on the redirect.
  useEffect(() => {
    if (!isLoading && !user) {
      login({
        org_code: KINDE_CONFIG.orgCode,
        app_state: {
          redirectTo: window.location.pathname + window.location.search,
        },
      });
    }
  }, [isLoading, user, login]);

  if (isLoading) {
    return <Loading label="Loading…" />;
  }
  if (!user) {
    // The useEffect above is firing the login redirect; this render
    // is just a placeholder while the browser navigates away.
    return <Loading label="Signing in…" />;
  }

  // Hard gate, two layers:
  //   1. The user's Kinde org must be in our role registry (today:
  //      only the InRange staff org is, with role=admin). Future
  //      orgs land in roles.ts.
  //   2. Belt + braces: email must end ``@inrange.io``. The org
  //      check is the real gate; the email check guards against an
  //      accidental non-employee account being added to the staff
  //      org in Kinde.
  // Both have to pass. The ``setTimeout`` matches admin-app's pattern
  // - calling logout synchronously during render misbehaves.
  const organisation = getOrganization();
  const role = roleForOrgCode(organisation?.orgCode);
  if (role === null || !user.email?.endsWith("@inrange.io")) {
    setTimeout(() => logout(), 100);
    return (
      <Loading label="Unauthorised - your account isn't in a recognised organisation. Signing out…" />
    );
  }

  return (
    <UserContext.Provider value={{ user, role, logout }}>
      {children}
    </UserContext.Provider>
  );
};
